<template>
	<v-container fluid class="pt-0">
		<v-row>
			<v-col cols="12" xl="12" lg="12" md="12">
				<v-tabs>
					<v-tab>{{ $t('tabs.basicInfo') }}</v-tab>
					<v-tab-item>
						<v-card flat outlined>
							<v-card-text>
								<v-row>
									<v-flex xs12 md4 lg4>
										<div :class="['text-h5', 'pa-2']">{{ $t('titles.classroomInformations') }}</div>
									</v-flex>
									<v-col class="d-flex justify-end">
										<v-btn
											v-if="!inEditMode"
											class="ml-2 mr-4"
											color="basicButton"
											@click="classroomExcelExport()"
											><v-icon small>fa-rectangle-list</v-icon>&nbsp;&nbsp;{{
												$t('buttons.classroomReport')
											}}
										</v-btn>
										<v-btn
											v-if="inEditMode"
											class="ml-2 mr-2"
											color="primary"
											@click="saveClick(null)"
										>
											<v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
										</v-btn>
										<v-btn
											v-if="!inEditMode"
											class="ml-2 mr-2"
											color="primary"
											@click="editClick()"
										>
											<v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
										</v-btn>
										<v-btn class="mr-2" color="basicButton" @click="backClick">
											<v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
										</v-btn>
									</v-col>
								</v-row>
								<v-row wrap>
									<v-flex lg6 xs12 sm12 class="pt-0">
										<v-col cols="12" v-if="inEditMode && $route.params.id != 0">
											<InputField
												:readonly="true"
												v-model="classroom.id"
												:label="'* ' + $t('fields.id')"
											></InputField>
										</v-col>
										<v-col cols="12">
											<InputField
												:readonly="!inEditMode"
												v-model="classroom.name"
												:errorBorder="!classroomValidation.name"
												:label="'* ' + $t('fields.name')"
											></InputField>
										</v-col>
										<v-col cols="12">
											<Dropdown
												:readonly="!inEditMode"
												v-model="classroom.office_id"
												:ajax="true"
												:data="'offices'"
												:errorBorder="!classroomValidation.office_id"
												:label="'* ' + $t('fields.office')"
											></Dropdown>
										</v-col>
										<v-col cols="12">
											<NumberField
												:readonly="!inEditMode"
												v-model="classroom.capacity"
												:errorBorder="!classroomValidation.capacity"
												:label="'* ' + $t('fields.capacity')"
											></NumberField>
										</v-col>
										<v-col cols="12">
											<CheckBox
												:readonly="!inEditMode"
												v-model="classroom.active"
												:label="$t('fields.active')"
											></CheckBox>
										</v-col>
									</v-flex>

									<v-flex lg6 xs12 sm12 class="pt-0 pl-2">
										<v-card flat>
											<v-card-text class="pa-0">
												<v-card-title class="pa-2 pl-0 pb-3">{{
													$t('titles.equipmentList')
												}}</v-card-title>
												<v-row v-if="inEditMode">
													<v-col cols="10" class="pt-0">
														<Dropdown2
															v-model="currentItem"
															:readonly="!inEditMode"
															:ajax="true"
															:clearable="true"
															:data="'equipments'"
															:label="$t('fields.equipment')"
															@item-selected="handleItemSelected"
														></Dropdown2>
													</v-col>
													<v-col cols="2" class="d-flex align-center">
														<v-tooltip bottom>
															<template v-slot:activator="{ on, attrs }">
																<v-btn
																	elevation="2"
																	small
																	:disabled="!inEditMode"
																	color="primary"
																	v-bind="attrs"
																	@click="addEquipmentToArray()"
																	v-on="on"
																>
																	<v-icon small>fa-plus</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('tooltips.add') }}</span>
														</v-tooltip>
													</v-col>
												</v-row>

												<v-container
													v-if="equipment.length > 0"
													class="pa-0 overflow-container"
												>
													<v-row v-for="(item, key) in equipment" :key="key" no-gutters>
														<v-col cols="10" class="pb-1 pt-0">
															<v-card
																:key="item.id"
																outlined
																:class="{ 'equipment-item': $vuetify.theme.dark }"
															>
																<div :class="['text-h8', 'pa-2']">{{ item.name }}</div>
															</v-card>
														</v-col>

														<v-col
															cols="2"
															v-if="inEditMode"
															class="pb-0 d-flex align-center"
														>
															<v-tooltip bottom>
																<template v-slot:activator="{ on, attrs }">
																	<v-btn
																		icon
																		large
																		v-bind="attrs"
																		@click="deleteEquipmentFromArray(item.id)"
																		v-on="on"
																	>
																		<v-icon small>fa-trash</v-icon>
																	</v-btn>
																</template>
																<span>{{ $t('tooltips.delete') }}</span>
															</v-tooltip>
														</v-col>
													</v-row>
												</v-container>
												<v-container v-if="equipment.length <= 0 && !inEditMode">
													<div :class="['text-body-1', 'pa-2', 'd-flex', 'justify-center']">
														{{ $t('titles.noEquipment') }}
													</div>
												</v-container>
											</v-card-text>
										</v-card>
									</v-flex>
								</v-row>
							</v-card-text>
						</v-card>
					</v-tab-item>
				</v-tabs>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import InputField from '../../trinkets/InputField';
import CheckBox from '../../trinkets/CheckBox';
import Classroom from '../../../models/Classroom';
import NumberField from '../../trinkets/NumberField';
import Dropdown from '../../trinkets/Dropdown';
import Dropdown2 from '../../trinkets/Dropdown2';

export default {
	name: 'Classroom',
	components: { CheckBox, InputField, NumberField, Dropdown, Dropdown2 },
	data: function () {
		return {
			classroom: new Classroom({ id: this.$route.params.id }),
			classroomValidation: new Classroom({}, true),
			equipment: [],
			initialLoad: false,
			inEditMode: false,
			lockKey: 'classroom',
			lockTimer: null,
			currentItem: null,
			selectedEquipment: {},
		};
	},
	methods: {
		editClick() {
			let vm = this;
			this.$setLock(this, this.lockKey + '|' + this.classroom.id, (data) => {
				if (data.success) {
					vm.inEditMode = true;
					vm.lockTimer = setInterval(() => {
						vm.$setLock(vm, vm.lockKey + '|' + vm.classroom.id);
					}, 10000);
				} else {
					vm.$alert(
						vm
							.$t('alerts.recordLocked')
							.split('%name%')
							.join(data.locked_by.first_name + ' ' + data.locked_by.last_name)
					);
				}
			});
		},
		backClick() {
			this.$router.go(-1);
		},
		handleItemSelected(item) {
			this.selectedEquipment.name = item.name;
			this.selectedEquipment.id = item.id;
		},
		addEquipmentToArray() {
			const exists = this.equipment.some((item) => item.id === this.selectedEquipment.id);

			if (!exists && Object.keys(this.selectedEquipment).length !== 0) {
				this.equipment.push(this.selectedEquipment);
				this.selectedEquipment = {};
				this.currentItem = null;
			}
		},
		deleteEquipmentFromArray(equipmentId) {
			this.equipment = this.equipment.filter((item) => item.id !== equipmentId);
		},
		saveClick(after = null) {
			let success = true;
			let errorMessages = '';

			if (this.classroom.name == null || this.classroom.name == '') {
				success = false;
				this.classroomValidation.name = false;
			} else {
				if (this.classroom.name.length > 50) {
					this.classroomValidation.name = false;
					errorMessages +=
						this.$t('alerts.lengthError')
							.replace('%field%', this.$t('fields.name'))
							.replace('%length%', '50') + '<br />';
				} else {
					this.classroomValidation.name = true;
				}
			}

			if (!this.classroom.office_id) {
				success = false;
				this.classroomValidation.office_id = false;
			} else {
				this.classroomValidation.office_id = true;
			}

			if (!this.classroom.capacity) {
				success = false;
				this.classroomValidation.capacity = false;
			} else {
				if (this.classroom.capacity < 1 || this.classroom.capacity > 100) {
					success = false;
					this.classroomValidation.capacity = false;
					errorMessages +=
						this.$t('alerts.valueError')
							.replace('%field%', this.$t('fields.capacity'))
							.replace('%min%', '0')
							.replace('%max%', '100') + '<br />';
				} else {
					this.classroomValidation.capacity = true;
				}
			}

			if (errorMessages !== '') {
				this.$alert(errorMessages);
				success = false;
			}

			if (success) {
				let vm = this;
				let action = 'post';
				let addition = '';
				let data = {
					classroom: this.classroom,
					equipment: this.equipment,
				};
				if (vm.classroom.id != 0) {
					action = 'put';
					addition = '/' + vm.classroom.id;
				}
				this.$APICall(action, 'classrooms' + addition, data, (data, success) => {
					if (success) {
						vm.inEditMode = false;
						if (vm.classroom.id == 0) {
							vm.$notify(vm.$t('alerts.basicAdd'));
						} else {
							vm.$notify(vm.$t('alerts.basicUpdate'));
						}
						if (after !== null) {
							after();
						} else {
							vm.backClick();
						}
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				});
			}
		},
		classroomExcelExport() {
			let vm = this;
			let data = {
				classroom: this.classroom,
				equipment: this.equipment,
			};
			this.$APICall('post', 'excel/classroom', data, (data, success) => {
				if (success) {
					window.open(data.link);
				} else {
					vm.$alert(vm.$t('alerts.basicError'));
				}
				false;
			});
		},
	},
	beforeRouteLeave(to, from, next) {
		let vm = this;
		if (this.inEditMode) {
			this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
				if (result) {
					if (vm.lockTimer !== null) clearInterval(vm.lockTimer);
					vm.$removeLock(vm, vm.lockKey + '|' + vm.classroom.id);
					next(true);
				} else {
					next(false);
				}
			});
		} else {
			if (this.lockTimer !== null) clearInterval(this.lockTimer);
			vm.$removeLock(vm, vm.lockKey + '|' + vm.classroom.id);
			next(true);
		}
	},
	mounted() {
		if (!this.$myStore.state.loggedIn) {
			this.$router.replace('/login');
		}

		let vm = this;
		if (this.classroom.id != 0) {
			this.$APICall('get', 'classrooms/' + this.classroom.id, {}, (data, success) => {
				if (success) {
					let usableData = JSON.parse(JSON.stringify(data));
					vm.classroom = usableData.classroom;
					vm.equipment = usableData.equipment;
					vm.$myStore.commit('setBreadcrumbs', [
						{
							text: vm.$t('modules.classrooms'),
							href: '/#/classrooms',
							disabled: false,
						},
						{
							text: vm.classroom.name + ' (' + vm.classroom.id + ')',
							href: '/#/classrooms/' + vm.classroom.id,
							disabled: true,
						},
					]);
					vm.$nextTick(() => {
						vm.initialLoad = true;
					});
				}
			});
		} else {
			this.$myStore.commit('setBreadcrumbs', [
				{
					text: this.$t('modules.classrooms'),
					href: '/#/classrooms',
					disabled: false,
				},
				{
					text: this.$t('modules.newClassroom'),
					href: '/#/classrooms/0',
					disabled: true,
				},
			]);
			this.inEditMode = true;
			this.initialLoad = true;
		}
	},
};
</script>

<style scoped>
.equipment-item {
	background: #444;
}

.overflow-container {
	max-height: 450px;
	overflow-y: auto;
}
</style>
